import { Field, FieldArray, Form, Formik, setIn } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/button/Button.jsx";
import { STTerminology } from "../../constants/terminology.js";
import { RichTextEditorField } from "../../components/form/RichTextEditor.jsx";
import { InputError, LevelReq } from "../../components/form/FormBody.jsx";
import IapImageCropperModal from "../../components/image/IapImageCropperModal.jsx";
import StoreSquarePreview from "../../components/image/StoreSquarePreview.jsx";
import instance from "../../utils/api_instance.util.js";
import { errorToastify, successToastify } from "../../utils/toastify.util.js";
import {
  buildHuntPackCompleteTouched,
  firstFormikErrorMessage,
  huntPackDraftSchema,
  huntPackSchema,
} from "../../utils/validation.util.js";
import { getAllCategories } from "../../features/categorySlice.js";
import { fetchSubCategoryDataByCatagoryId } from "../../features/subCategorySlice.js";
import { huntPackDetailsById, updateHuntPackItemPositionsAPI } from "../../apis/huntPack.api.js";
import {
  createHuntPack,
  setHuntPackRedirect,
  setSelectedHuntPack,
  updateHuntPack,
} from "../../features/huntPackSlice.js";
import DraggableItemCard from "../item/DraggableItemCard.jsx";

const MultiSelectDropdown = ({
  label,
  placeholder = "Search...",
  options = [],
  selectedIds = [],
  onToggle,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const selectedText = useMemo(() => {
    const count = selectedIds.length || 0;
    return count ? `${count} Selected` : `Select ${label}`;
  }, [selectedIds, label]);

  const filtered = useMemo(() => {
    const query = (search || "").trim().toLowerCase();
    if (!query) return options;

    return options.filter((option) =>
      String(option?.name || option?.title || "")
        .toLowerCase()
        .includes(query),
    );
  }, [options, search]);

  useEffect(() => {
    const handler = (event) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="col-md-6 mb-3" ref={wrapRef}>
      <label className="form-label fs-7">{label}</label>

      <div className="category-dd">
        <div
          className={`form-control category-dd-input ${disabled ? "bg-light" : ""}`}
          role="button"
          onClick={() => {
            if (disabled) return;
            setOpen((value) => !value);
          }}
          style={{
            pointerEvents: disabled ? "none" : "auto",
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <span>{selectedText}</span>
          <span className="category-dd-arrow">{open ? "▲" : "▼"}</span>
        </div>

        {open ? (
          <div className="category-dd-panel">
            <input
              type="text"
              className="form-control mb-2"
              placeholder={placeholder}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            <div className="category-dd-list">
              {filtered.length === 0 ? (
                <div className="text-muted small">No data found</div>
              ) : (
                <div className="category-dd-grid">
                  {filtered.map((item) => (
                    <label key={item.id} className="category-dd-item">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(String(item.id))}
                        onChange={() => onToggle(String(item.id))}
                      />
                      <span>{item.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="category-dd-footer">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const createEmptyItem = () => ({
  itemname: "",
  description: "",
  imageurl: "",
});

const createBlankItems = (count = 1) =>
  Array.from({ length: count }, () => createEmptyItem());

const normalizeItemsForRender = (items, fallbackCount = 1) => {
  const normalized = Array.isArray(items)
    ? items
        .filter(Boolean)
        .map((item) => ({
          ...createEmptyItem(),
          ...item,
        }))
    : [];

  return normalized.length ? normalized : createBlankItems(fallbackCount);
};

const normalizeIdArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).filter(Boolean);
  }

  if (typeof value === "string" && value.includes(",")) {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  if (value !== null && value !== undefined && value !== "") {
    return [String(value)];
  }

  return [];
};

const moveArrayItem = (items, fromIndex, toIndex) => {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
};

const buildOptionsWithSelected = (options = [], selectedIds = []) => {
  const optionMap = new Map(
    (options || []).map((option) => [String(option.id), { id: String(option.id), name: option.name }]),
  );

  (selectedIds || []).forEach((selectedId) => {
    const normalizedId = String(selectedId);
    if (!normalizedId || optionMap.has(normalizedId)) return;
    optionMap.set(normalizedId, {
      id: normalizedId,
      name: `Selected (${normalizedId})`,
    });
  });

  return Array.from(optionMap.values());
};

const initialHuntPackData = {
  id: "",
  title: "",
  description: "",
  imageurl: "",
  price: "",
  category_ids: [],
  subcategory_ids: [],
  status: 3,
  items: createBlankItems(5),
};


const HuntPackFormPage = ({ mode = "create" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isBtnLoad, isRedirect, selectedHuntPack } = useSelector((state) => state.huntPack);
  const { allCategory } = useSelector((state) => state.category);
  const [subPool, setSubPool] = useState([]);

  const [huntPackData, setHuntPackData] = useState(initialHuntPackData);
  const [deleteItemIds, setDeleteItemIds] = useState([]);
  const [submitMode, setSubmitMode] = useState("draft");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isSavingItemOrder, setIsSavingItemOrder] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(mode === "edit");
  const [mainImageCropState, setMainImageCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "hunt-pack-image.jpg",
  });
  const [itemImageCropState, setItemImageCropState] = useState({
    isOpen: false,
    imageSrc: "",
    fileName: "hunt-item-image.jpg",
    itemIndex: null,
  });
  const fileInputRefs = useRef({});
  const itemInputRefs = useRef({});
  const mainImageFieldSetterRef = useRef(null);
  const itemImageFieldSetterRef = useRef(null);

  const isEditMode = mode === "edit";
  const huntPackId = location.state?.id || selectedHuntPack?.id || "";

  useEffect(() => {
    dispatch(setHuntPackRedirect(false));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllCategories());
  }, [dispatch]);

  useEffect(() => {
    if (isRedirect) {
      navigate("/hunt-packs");
    }
  }, [isRedirect, navigate]);

  useEffect(() => {
    if (!isEditMode) {
      setHuntPackData(initialHuntPackData);
      setDeleteItemIds([]);
      setSelectedCategories([]);
      setSubPool([]);
      setIsInitialLoading(false);
      return;
    }

    const fromState = location.state?.huntPack;
    if (fromState) {
      dispatch(setSelectedHuntPack(fromState));
    }
  }, [dispatch, isEditMode, location.state]);

  useEffect(() => {
    if (!selectedCategories.length) {
      setSubPool([]);
      return;
    }

    const fetchAndMergeSubcategories = async () => {
      try {
        const results = await Promise.all(
          selectedCategories.map((categoryId) =>
            dispatch(fetchSubCategoryDataByCatagoryId({ category_id: categoryId })),
          ),
        );

        let merged = [];
        results.forEach((result) => {
          const payload = result?.payload;
          const list =
            payload?.result?.list ||
            payload?.result?.data ||
            payload?.result ||
            payload?.data ||
            payload ||
            [];

          if (Array.isArray(list)) {
            merged = merged.concat(list);
          }
        });

        const mergedMap = new Map();
        merged.forEach((item) => {
          const id = Number(item?.id);
          if (!id || mergedMap.has(id)) return;
          mergedMap.set(id, { id, name: item?.name || `Sub ${id}` });
        });

        setSubPool(Array.from(mergedMap.values()));
      } catch (error) {
        console.error(error);
        setSubPool([]);
      }
    };

    fetchAndMergeSubcategories();
  }, [dispatch, selectedCategories]);

  useEffect(() => {
    if (!isEditMode || !huntPackId) return;

    let ignore = false;
    setIsInitialLoading(true);

    const loadHuntPackDetails = async () => {
      try {
        const response = await huntPackDetailsById({ id: huntPackId });
        if (!response?.success) {
          throw new Error(response?.message || "Failed to load hunt pack");
        }

        if (ignore) return;
        const source = response.result || {};
        const categoryIds = normalizeIdArray(source.category_ids);
        const subcategoryIds = normalizeIdArray(source.subcategory_ids);
        const normalizedItems = normalizeItemsForRender(
          Array.isArray(source.items)
            ? [...source.items]
                .filter(Boolean)
                .sort((a, b) => Number(a?.sequence || 0) - Number(b?.sequence || 0))
                .map((item) => ({
                  id: item.id,
                  itemname: item.itemname || "",
                  description: item.description || "",
                  imageurl: item.imageurl || "",
                  sequence: item.sequence ?? null,
                }))
            : [],
          1,
        );

        setHuntPackData({
          id: source.id,
          title: source.title || "",
          description: source.description || "",
          imageurl: source.imageurl || "",
          price: source.price ?? "",
          category_ids: categoryIds,
          subcategory_ids: subcategoryIds,
          status: Number(source.status) === 3 ? 3 : Number(source.status) || 0,
          items: normalizedItems,
        });
        setSubmitMode(Number(source.status) === 3 ? "draft" : "complete");
        setSelectedCategories(categoryIds);
        setDeleteItemIds([]);
      } catch (error) {
        if (!ignore) {
          errorToastify(error?.response?.data?.message || error?.message || "Failed to load hunt pack details");
        }
      } finally {
        if (!ignore) {
          setIsInitialLoading(false);
        }
      }
    };

    loadHuntPackDetails();

    return () => {
      ignore = true;
    };
  }, [isEditMode, huntPackId]);

  const triggerMainImage = () => {
    fileInputRefs.current.main?.click();
  };

  const triggerItemImage = (index) => {
    itemInputRefs.current[index]?.click();
  };

  const buildCroppedFileName = (originalName, prefix) => {
    const safePrefix = prefix || "cropped-image";
    const sourceName = String(originalName || "").trim();
    const baseName = sourceName.replace(/\.[^/.]+$/, "") || safePrefix;
    const normalizedBase = baseName
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();

    return `${normalizedBase || safePrefix}-${Date.now()}.jpg`;
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await instance.post("/api/v1/common/file/upload", formData);
    if (!data?.success) {
      throw new Error(data?.message || "Upload failed");
    }
    const url =
      typeof data?.result === "string"
        ? data.result
        : data?.result?.url || data?.result?.secure_url;
    if (!url) throw new Error("Upload failed: missing image URL");
    return url;
  };

  const closeMainImageCropper = () => {
    if (mainImageCropState.imageSrc) {
      URL.revokeObjectURL(mainImageCropState.imageSrc);
    }
    setMainImageCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "hunt-pack-image.jpg",
    });
  };

  const uploadMainImageBlob = async (blob) => {
    try {
      const uploadFileBlob = new File(
        [blob],
        buildCroppedFileName(mainImageCropState.fileName, "hunt-pack-image"),
        { type: "image/jpeg" },
      );
      const url = await uploadFile(uploadFileBlob);
      mainImageFieldSetterRef.current?.("imageurl", url);
      successToastify("Image uploaded");
      closeMainImageCropper();
    } catch (err) {
      errorToastify(err?.response?.data?.message || err.message || "Image upload failed");
    }
  };

  const handleMainImageUpload = async (event, setFieldValue) => {
    try {
      const file = event.currentTarget.files?.[0];
      if (!file) return;
      mainImageFieldSetterRef.current = setFieldValue;
      setMainImageCropState({
        isOpen: true,
        imageSrc: URL.createObjectURL(file),
        fileName: file.name || "hunt-pack-image.jpg",
      });
    } finally {
      event.target.value = "";
    }
  };

  const closeItemImageCropper = () => {
    if (itemImageCropState.imageSrc) {
      URL.revokeObjectURL(itemImageCropState.imageSrc);
    }
    setItemImageCropState({
      isOpen: false,
      imageSrc: "",
      fileName: "hunt-item-image.jpg",
      itemIndex: null,
    });
  };

  const uploadItemImageBlob = async (blob) => {
    try {
      const uploadFileBlob = new File(
        [blob],
        buildCroppedFileName(itemImageCropState.fileName, "hunt-item-image"),
        { type: "image/jpeg" },
      );
      const url = await uploadFile(uploadFileBlob);
      if (itemImageCropState.itemIndex !== null) {
        itemImageFieldSetterRef.current?.(
          `items.${itemImageCropState.itemIndex}.imageurl`,
          url,
        );
      }
      successToastify("Challenge image uploaded");
      closeItemImageCropper();
    } catch (err) {
      errorToastify(err?.response?.data?.message || err.message || "Challenge image upload failed");
    }
  };

  const handleItemImageUpload = async (event, setFieldValue, index) => {
    try {
      const file = event.currentTarget.files?.[0];
      if (!file) return;
      itemImageFieldSetterRef.current = setFieldValue;
      setItemImageCropState({
        isOpen: true,
        imageSrc: URL.createObjectURL(file),
        fileName: file.name || "hunt-item-image.jpg",
        itemIndex: index,
      });
    } finally {
      event.target.value = "";
    }
  };


  const removeExistingItem = (item, remove, index) => {
    if (item?.id) {
      setDeleteItemIds((prev) => [...new Set([...prev, Number(item.id)])]);
    }
    remove(index);
  };

  const handleSaveItemOrder = async (items = []) => {
    if (!isEditMode || !huntPackId) return;

    const persistedItemIds = items
      .map((item) => Number(item?.id))
      .filter(Boolean);

    if (!persistedItemIds.length) {
      successToastify("Challenge order will be saved when you save the hunt pack");
      return;
    }

    try {
      setIsSavingItemOrder(true);
      const response = await updateHuntPackItemPositionsAPI({
        huntId: Number(huntPackId),
        itemOrder: persistedItemIds,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to update challenge order");
      }

      successToastify("Challenge order updated successfully");
    } catch (error) {
      errorToastify(error?.response?.data?.message || error?.message || "Failed to update challenge order");
    } finally {
      setIsSavingItemOrder(false);
    }
  };

  const onSubmitHandler = (values, mode) => {
    const isDraft = mode === "draft";
    const cleanedItems = Array.isArray(values.items)
      ? values.items
          .map((item, index) => ({
            ...(item.id ? { id: item.id } : {}),
            itemname: item.itemname?.trim?.() || "",
            description: item.description || "",
            imageurl: item.imageurl || "",
            sequence: index + 1,
          }))
          .filter((item) => Boolean(item.itemname?.trim?.()))
      : [];

    const payload = {
      ...values,
      price: values.price,
      status: isDraft ? 3 : 1,
      category_ids: Array.isArray(values.category_ids) ? values.category_ids.map(Number) : [],
      subcategory_ids: Array.isArray(values.subcategory_ids) ? values.subcategory_ids.map(Number) : [],
      items: cleanedItems,
    };

    if (isEditMode) {
      payload.deleteItemIds = deleteItemIds;
      dispatch(updateHuntPack(payload));
    } else {
      delete payload.id;
      dispatch(createHuntPack(payload));
    }
  };

  const pageTitle = isEditMode ? "Edit Hunt Pack" : "Add Hunt Pack";
  const categoryOptions = useMemo(
    () => (Array.isArray(allCategory) ? allCategory.map((item) => ({ id: String(item.id), name: item.name })) : []),
    [allCategory],
  );
  const subCategoryOptions = useMemo(
    () => buildOptionsWithSelected(Array.isArray(subPool) ? subPool : [], huntPackData.subcategory_ids),
    [huntPackData.subcategory_ids, subPool],
  );
  const previewItems = useMemo(
    () => normalizeItemsForRender(huntPackData.items, isEditMode ? 1 : 5),
    [isEditMode, huntPackData.items],
  );

  const formKey = useMemo(() => {
    const itemSignature = previewItems.map((item) => item.id || item.itemname || "new").join("-");
    return `${mode}-${huntPackId || "new"}-${itemSignature}`;
  }, [mode, huntPackId, previewItems]);

  if (isEditMode && isInitialLoading) {
    return (
      <div id="main" className="main">
        <div className="pagetitle">
          <h1>{pageTitle}</h1>
        </div>
        <section className="section">
          <div className="container">
            <div className="text-center py-5">Loading hunt pack...</div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div id="main" className="main">
      <div className="pagetitle">
        <h1>{pageTitle}</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/hunt-packs">Hunt Packs</Link>
            </li>
            <li className="breadcrumb-item active">{isEditMode ? "Edit" : "Add"}</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="container">
          <div className="row">
            <div className="col-md-9">
              <Formik
                key={formKey}
                enableReinitialize
                initialValues={{
                  ...huntPackData,
                  items: previewItems,
                }}
                validationSchema={submitMode === "complete" ? huntPackSchema : huntPackDraftSchema}
                validateOnChange={submitMode === "complete"}
                validateOnBlur={submitMode === "complete"}
                onSubmit={(vals) => onSubmitHandler(vals, submitMode)}
              >
                {({ values, setFieldValue, submitForm, setErrors, setTouched }) => (
                  <Form>
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <LevelReq htmlFor="title" className="form-label fs-7">
                          Hunt Pack Name
                        </LevelReq>
                        <Field type="text" className="form-control" id="title" name="title" />
                        <InputError name="title" />
                      </div>
                    </div>

                    <div className="row mb-1">
                      <MultiSelectDropdown
                        label="Collections"
                        placeholder="Search collection..."
                        options={categoryOptions}
                        selectedIds={Array.isArray(values.category_ids) ? values.category_ids : []}
                        onToggle={(categoryId) => {
                          const current = Array.isArray(values.category_ids) ? values.category_ids : [];
                          const next = current.includes(categoryId)
                            ? current.filter((entry) => entry !== categoryId)
                            : [...current, categoryId];

                          setFieldValue("category_ids", next);
                          setFieldValue("subcategory_ids", []);
                          setSelectedCategories(next);
                        }}
                      />

                      <MultiSelectDropdown
                        label="Sub-category"
                        placeholder="Search sub-category..."
                        options={buildOptionsWithSelected(subCategoryOptions, values.subcategory_ids)}
                        selectedIds={Array.isArray(values.subcategory_ids) ? values.subcategory_ids : []}
                        onToggle={(subCategoryId) => {
                          const current = Array.isArray(values.subcategory_ids) ? values.subcategory_ids : [];
                          const next = current.includes(subCategoryId)
                            ? current.filter((entry) => entry !== subCategoryId)
                            : [...current, subCategoryId];

                          setFieldValue("subcategory_ids", next);
                        }}
                        disabled={!Array.isArray(values.category_ids) || values.category_ids.length === 0}
                      />

                      <div className="col-md-6">
                        <InputError name="category_ids" />
                      </div>
                      <div className="col-md-6">
                        <InputError name="subcategory_ids" />
                      </div>
                    </div>



                    <div className="row mb-3">
                      <div className="col-md-12">
                        <LevelReq className="form-label fs-7">
                          Description
                        </LevelReq>
                        <RichTextEditorField name="description" placeholder="Enter description..." />
                        <InputError name="description" />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <LevelReq htmlFor="price" className="form-label fs-7">
                          Price
                        </LevelReq>
                        <Field type="text" className="form-control" id="price" name="price" min="0" />
                        <InputError name="price" />
                      </div>
                      
                    </div>

                    <div className="row mb-4">
                      <div className="col-md-12">
                        <div
                          className="card border-0"
                          style={{
                            backgroundColor: "#f8fafc",
                            border: "1px solid rgba(15, 23, 42, 0.08)",
                          }}
                        >
                          <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                              <div>
                                <LevelReq className="form-label fs-6 fw-semibold mb-1">
                                  Hunt Pack Image
                                </LevelReq>
                                <div className="text-muted small">
                                  Aspect ratio <span className="fw-semibold text-dark">16:9</span>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <input
                                  ref={(node) => {
                                    fileInputRefs.current.main = node;
                                  }}
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={(event) => handleMainImageUpload(event, setFieldValue)}
                                />
                                <Button type="button" className="btn btn-outline-primary btn-sm" onClick={triggerMainImage}>
                                  <i className="bi bi-upload me-1" />
                                  {values.imageurl ? "Replace Image" : "Upload Image"}
                                </Button>
                                {values.imageurl ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => setFieldValue("imageurl", "")}
                                  >
                                    <i className="bi bi-trash3 me-1" />
                                    Remove
                                  </button>
                                ) : null}
                              </div>
                            </div>

                            {values.imageurl ? (
                              <div
                                className="d-flex align-items-start gap-4 flex-wrap pt-3"
                                style={{ borderTop: "1px solid rgba(15, 23, 42, 0.08)" }}
                              >
                                <div style={{ width: "220px" }}>
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        padding: "2px 9px",
                                        borderRadius: "999px",
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        letterSpacing: "0.02em",
                                        backgroundColor: "#e0e7ff",
                                        color: "#4338ca",
                                      }}
                                    >
                                      16:9
                                    </span>
                                    <span className="fw-semibold" style={{ color: "#334155", fontSize: "0.9rem" }}>
                                      Upload Preview
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      width: "100%",
                                      aspectRatio: "16 / 9",
                                      borderRadius: "10px",
                                      overflow: "hidden",
                                      border: "1px solid rgba(15, 23, 42, 0.12)",
                                      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 12px rgba(15, 23, 42, 0.08)",
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    <img
                                      src={values.imageurl}
                                      alt="Hunt Pack"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                      }}
                                    />
                                  </div>
                                </div>

                                <div
                                  className="d-none d-md-block"
                                  style={{ width: "1px", alignSelf: "stretch", backgroundColor: "rgba(15, 23, 42, 0.08)" }}
                                />

                                <StoreSquarePreview imageSrc={values.imageurl} />
                              </div>
                            ) : (
                              <div
                                className="text-center text-muted"
                                style={{
                                  padding: "32px 14px",
                                  borderRadius: "10px",
                                  border: "1px dashed rgba(15, 23, 42, 0.2)",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <i className="bi bi-image" style={{ fontSize: "1.75rem", opacity: 0.5 }} />
                                <div className="small mt-2">No image uploaded yet.</div>
                              </div>
                            )}

                            <div className="text-muted small mt-3 d-flex align-items-center gap-1">
                              <i className="bi bi-info-circle" />
                              Square preview is only for positioning. It does not save a second image.
                            </div>
                            <InputError name="imageurl" />
                          </div>
                        </div>
                        <IapImageCropperModal
                          isOpen={mainImageCropState.isOpen}
                          imageSrc={mainImageCropState.imageSrc}
                          onCancel={closeMainImageCropper}
                          onConfirm={uploadMainImageBlob}
                          title="Crop Hunt Pack Image"
                          targetWidth={16}
                          targetHeight={9}
                          useSourceDimensions
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Hunt Challenges</h5>
                      <div className="d-flex gap-2">
                        {isEditMode ? (
                          <Button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            disabled={isSavingItemOrder}
                            onClick={() => handleSaveItemOrder(values.items)}
                          >
                            {isSavingItemOrder ? "Saving Order..." : "Save Challenge Order"}
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() =>
                            setFieldValue("items", [
                              ...normalizeItemsForRender(values.items, isEditMode ? 1 : 5),
                              createEmptyItem(),
                            ])
                          }
                        >
                          Add Challenge
                        </Button>
                      </div>
                    </div>

                    <FieldArray name="items">
                      {({ remove, push }) => (
                        <>
                          {normalizeItemsForRender(values.items, isEditMode ? 1 : 5).map((item, index) => (
                            <DraggableItemCard
                              key={`hunt-item-${item.id || index}`}
                              index={index}
                              item={item}
                              moveItem={(fromIndex, toIndex) => {
                                setFieldValue(
                                  "items",
                                  moveArrayItem(
                                    normalizeItemsForRender(values.items, isEditMode ? 1 : 5),
                                    fromIndex,
                                    toIndex,
                                  ),
                                );
                              }}
                            >
                              <div className="card mb-3">
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="card-title mb-0 d-flex align-items-center">
                                      Challenge {index + 1}
                                      <span className="ms-2 d-flex gap-1">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          title="Move up"
                                          disabled={index === 0}
                                          onClick={() =>
                                            setFieldValue(
                                              "items",
                                              moveArrayItem(
                                                normalizeItemsForRender(values.items, isEditMode ? 1 : 5),
                                                index,
                                                index - 1,
                                              ),
                                            )
                                          }
                                        >
                                          ▲
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          title="Move down"
                                          disabled={
                                            index === normalizeItemsForRender(values.items, isEditMode ? 1 : 5).length - 1
                                          }
                                          onClick={() =>
                                            setFieldValue(
                                              "items",
                                              moveArrayItem(
                                                normalizeItemsForRender(values.items, isEditMode ? 1 : 5),
                                                index,
                                                index + 1,
                                              ),
                                            )
                                          }
                                        >
                                          ▼
                                        </button>
                                      </span>
                                    </h6>
                                    {values.items.length > 1 ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => removeExistingItem(item, remove, index)}
                                      >
                                        Remove Challenge
                                      </button>
                                    ) : null}
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-6">
                                      <LevelReq htmlFor={`items.${index}.itemname`} className="form-label fs-7">
                                        Challenge Name
                                      </LevelReq>
                                      <Field
                                        type="text"
                                        className="form-control"
                                        id={`items.${index}.itemname`}
                                        name={`items.${index}.itemname`}
                                      />
                                      <InputError name={`items.${index}.itemname`} />
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label fs-7">
                                        Challenge Description (optional)
                                      </label>
                                      <RichTextEditorField name={`items.${index}.description`} minHeight="150px" />
                                      <InputError name={`items.${index}.description`} />
                                    </div>
                                  </div>

                                  <div className="row">
                                    <div className="col-md-12">
                                      <LevelReq className="form-label fs-7">
                                        Challenge Image (Aspect Ratio :- <b>1:1</b>)
                                      </LevelReq>
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <input
                                          ref={(node) => {
                                            itemInputRefs.current[index] = node;
                                          }}
                                          type="file"
                                          accept="image/*"
                                          style={{ display: "none" }}
                                          onChange={(event) => handleItemImageUpload(event, setFieldValue, index)}
                                        />
                                        <Button
                                          type="button"
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => triggerItemImage(index)}
                                        >
                                          Upload Challenge Image
                                        </Button>
                                        {item.imageurl ? (
                                          <>
                                            <div
                                              style={{
                                                width: "72px",
                                                aspectRatio: "1 / 1",
                                                borderRadius: "10px",
                                                overflow: "hidden",
                                                border: "1px solid rgba(15, 23, 42, 0.12)",
                                                backgroundColor: "#f8fafc",
                                              }}
                                            >
                                              <img
                                                src={item.imageurl}
                                                alt={`${STTerminology.challenge} ${index + 1}`}
                                                style={{
                                                  width: "100%",
                                                  height: "100%",
                                                  objectFit: "cover",
                                                  display: "block",
                                                }}
                                              />
                                            </div>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger"
                                              onClick={() => setFieldValue(`items.${index}.imageurl`, "")}
                                            >
                                              Remove
                                            </button>
                                          </>
                                        ) : null}
                                      </div>
                                      <InputError name={`items.${index}.imageurl`} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </DraggableItemCard>
                          ))}

                          <button
                            type="button"
                            className="btn btn-link ps-0"
                            onClick={() => push(createEmptyItem())}
                          >
                            + Add another challenge
                          </button>
                        </>
                      )}
                    </FieldArray>

                    <IapImageCropperModal
                      isOpen={itemImageCropState.isOpen}
                      imageSrc={itemImageCropState.imageSrc}
                      onCancel={closeItemImageCropper}
                      onConfirm={uploadItemImageBlob}
                      title="Crop Hunt Challenge Image"
                      targetWidth={1}
                      targetHeight={1}
                      useSourceDimensions
                    />

                    <div className="mt-4 d-flex gap-2">
                      <Button
                        type="button"
                        className="btn btn-outline-secondary w-50"
                        disabled={isBtnLoad}
                        onClick={() => {
                          flushSync(() => {
                            setSubmitMode("draft");
                            setErrors({});
                            setTouched({});
                          });
                          submitForm();
                        }}
                      >
                        {isBtnLoad && submitMode === "draft" ? "Saving..." : "Save as Draft"}
                      </Button>
                      <Button
                        type="button"
                        className="btn btn-primary w-50"
                        disabled={isBtnLoad}
                        onClick={async () => {
                          try {
                            await huntPackSchema.validate(values, {
                              abortEarly: false,
                            });
                          } catch (err) {
                            const formErrors = {};
                            if (Array.isArray(err.inner) && err.inner.length) {
                              err.inner.forEach((validationErr) => {
                                if (validationErr.path) {
                                  setIn(
                                    formErrors,
                                    validationErr.path,
                                    validationErr.message,
                                  );
                                }
                              });
                            } else if (err.message) {
                              formErrors.form = err.message;
                            }
                            setErrors(formErrors);
                            setTouched(
                              buildHuntPackCompleteTouched(values),
                              false,
                            );
                            errorToastify(
                              firstFormikErrorMessage(formErrors) ||
                                "Please complete all required fields.",
                            );
                            return;
                          }
                          flushSync(() => setSubmitMode("complete"));
                          setTouched(buildHuntPackCompleteTouched(values), false);
                          submitForm();
                        }}
                      >
                        {isBtnLoad && submitMode === "complete"
                          ? "Saving..."
                          : isEditMode
                            ? "Complete Update"
                            : "Complete"}
                      </Button>
                      <Button type="button" className="btn btn-secondary" onClick={() => navigate("/hunt-packs")}>
                        Cancel
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HuntPackFormPage;
